import { useState, useCallback } from 'react';
import { useServices } from '@/di/precon-supporter';
import type { ImportResult } from '@/application/precon-supporter/usecases/data-transfer/ImportDataUseCase';

/** インポートファイルの最大サイズ（5KB） */
const MAX_IMPORT_FILE_SIZE = 5 * 1024;

/**
 * エクスポートファイル名を生成
 */
const generateExportFileName = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `precon-supporter-data-${year}-${month}-${day}.dat`;
};

/**
 * インポート成功時の結果
 */
export interface ImportDataResult {
  success: true;
  /** マイグレーションが実行されたかどうか */
  migrated: boolean;
  /** 元のデータのバージョン */
  originalVersion: number;
}

/**
 * インポート失敗時の結果
 */
export interface ImportDataError {
  success: false;
  error: Error;
}

/**
 * インポート結果の型
 */
export type ImportDataOutcome = ImportDataResult | ImportDataError;

/**
 * データ転送（エクスポート/インポート）フック
 *
 * 受講履歴データのエクスポート・インポート機能を提供する。
 * データは暗号化されたバイナリ形式（.dat）で保存される。
 */
export const useDataTransfer = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const services = useServices();
  const { exportDataUseCase, importDataUseCase, encryptionService } = services;

  /**
   * データをエクスポート（暗号化されたバイナリファイルとしてダウンロード）
   */
  const exportData = useCallback(async (): Promise<boolean> => {
    try {
      setExporting(true);
      setError(null);

      // エクスポートデータを取得
      const data = await exportDataUseCase.execute();

      // データを暗号化
      const encryptedBlob = await encryptionService.encrypt(data);

      // バイナリファイルとしてダウンロード
      const url = URL.createObjectURL(encryptedBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = generateExportFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('エクスポートに失敗しました');
      setError(error);
      return false;
    } finally {
      setExporting(false);
    }
  }, [exportDataUseCase, encryptionService]);

  /**
   * ファイルからデータをインポート
   * @param file インポートする暗号化ファイル（.dat）
   * @returns インポート結果（成功時はマイグレーション情報を含む）
   */
  const importData = useCallback(
    async (file: File): Promise<ImportDataOutcome> => {
      try {
        setImporting(true);
        setError(null);

        // ファイルサイズチェック
        if (file.size > MAX_IMPORT_FILE_SIZE) {
          throw new Error(
            'ファイルサイズが大きすぎます。正しい受講履歴データファイルを選択してください。'
          );
        }

        // ファイルをArrayBufferとして読み込み
        const arrayBuffer = await file.arrayBuffer();

        // 復号化（InvalidFileFormatError と DecryptionError はそのままユーザー向けメッセージとして使用）
        const jsonData = await encryptionService.decrypt(arrayBuffer);

        // インポート実行（バリデーションとマイグレーションはユースケース内で行われる）
        const result: ImportResult = await importDataUseCase.execute(jsonData);

        return {
          success: true,
          migrated: result.migrated,
          originalVersion: result.originalVersion,
        };
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('インポートに失敗しました');
        setError(error);
        return { success: false, error };
      } finally {
        setImporting(false);
      }
    },
    [importDataUseCase, encryptionService]
  );

  return {
    exportData,
    importData,
    exporting,
    importing,
    loading: exporting || importing,
    error,
  };
};
